import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Upload, FileWarning, AlertTriangle, Users, Clock, Activity,
  TrendingDown, ChevronDown, ChevronUp, Search, RefreshCw,
  FileDown, Bell, X, Calendar,
} from 'lucide-react';
import {
  fetchSiniestStatus, fetchSiniestKpis, fetchSiniestMensual, fetchSiniestPorZona,
  fetchSiniestPropios, fetchSiniestPorTipo, fetchSiniestPorRiesgo, fetchSiniestPorLugar,
  fetchSiniestFilterGerentes, fetchSiniestFilterZonas,
  exportSiniestPDF, fetchSiniestAlertas,
  fetchSiniestComparativaAnual,
} from '@/services/api';
import { GlobalFilterBar } from '@/components/dashboard/GlobalFilterBar';
import { KpiGlowCard } from '@/components/dashboard/KpiGlowCard';
import { ChartCard } from '@/components/dashboard/ChartCard';
import { TrendLineChart, HorizontalBarChart, DonutChart, VerticalBarChart } from '@/components/dashboard/Charts';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useUserStore } from '@/store/userStore';
import { useFiltersStore } from '@/store/filtersStore';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartTooltip,
  ResponsiveContainer, Legend, ReferenceLine
} from 'recharts';
import { AlertasPanel } from '@/components/siniestralidad/AlertasPanel';
import { SiniestKpiCard } from '@/components/siniestralidad/SiniestKpiCard';
import { ImportPanel } from '@/components/siniestralidad/ImportPanel';

const TIPO_COLOR = {
  'Accidente con Baja': 'var(--vz-red)',
  'Accidente Sin Baja': 'var(--vz-orange)',
  'Cuasi incidentes / Near Misses': 'var(--vz-yellow)',
  'Accidente In Itinere': '#AB47BC',
};


export default function Siniestralidad() {
  const { user, token } = useUserStore();
  const globalDesde = useFiltersStore(s => s.desde);
  const globalHasta = useFiltersStore(s => s.hasta);
  const currentYear = new Date().getFullYear();
  const [anoFilter, setAnoFilter] = useState(currentYear);
  const [mesFilter, setMesFilter] = useState('');
  const [gerenteFilter, setGerenteFilter] = useState('');
  const [zonaFilter, setZonaFilter] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  const [lugarFilter, setLugarFilter] = useState('');
  const [personaFilter, setPersonaFilter] = useState('');
  const [gerenteZonaFilter, setGerenteZonaFilter] = useState('');
  const [search, setSearch] = useState('');
  const [desdeFilter, setDesdeFilter] = useState('');
  const [hastaFilter, setHastaFilter] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showAlertas, setShowAlertas] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [page, setPage] = useState(1);

  const kpiParams = {
    ano: anoFilter,
    ...(gerenteFilter ? { gerente_zona: gerenteFilter } : {}),
    ...(zonaFilter ? { zona: zonaFilter } : {}),
  };
  const propParams = {
    ano: anoFilter,
    ...(mesFilter ? { mes: Number(mesFilter) } : {}),
    ...(tipoFilter ? { tipo: tipoFilter } : {}),
    ...(lugarFilter ? { lugar: lugarFilter } : {}),
    ...(gerenteZonaFilter ? { gerente_zona: gerenteZonaFilter } : {}),
    ...(personaFilter ? { persona: personaFilter } : {}),
    ...(search ? { riesgo: search } : {}),
    // Local date filter overrides global
    ...(desdeFilter ? { desde: desdeFilter } : globalDesde ? { desde: globalDesde } : {}),
    ...(hastaFilter ? { hasta: hastaFilter } : globalHasta ? { hasta: globalHasta } : {}),
    page,
    limit: 50,
  };

  const enabled = !!token;
  const { data: status, isLoading: statusLoading } = useQuery({ queryKey: ['siniest', 'status'], queryFn: fetchSiniestStatus, staleTime: 30000, enabled });
  const { data: kpis } = useQuery({ queryKey: ['siniest', 'kpis', kpiParams], queryFn: () => fetchSiniestKpis(kpiParams), staleTime: 60000, enabled });
  const { data: mensual = [] } = useQuery({ queryKey: ['siniest', 'mensual', kpiParams], queryFn: () => fetchSiniestMensual(kpiParams), staleTime: 60000, enabled });
  const { data: porZona = [] } = useQuery({ queryKey: ['siniest', 'zona', kpiParams], queryFn: () => fetchSiniestPorZona(kpiParams), staleTime: 60000, enabled });
  const { data: propios } = useQuery({ queryKey: ['siniest', 'propios', propParams], queryFn: () => fetchSiniestPropios(propParams), staleTime: 60000, enabled });
  const { data: porTipo = [] } = useQuery({ queryKey: ['siniest', 'tipo', kpiParams], queryFn: () => fetchSiniestPorTipo(kpiParams), staleTime: 60000, enabled });
  const { data: porRiesgo = [] } = useQuery({ queryKey: ['siniest', 'riesgo', kpiParams], queryFn: () => fetchSiniestPorRiesgo(kpiParams), staleTime: 60000, enabled });
  const { data: porLugar = [] } = useQuery({ queryKey: ['siniest', 'lugar', kpiParams], queryFn: () => fetchSiniestPorLugar(kpiParams), staleTime: 60000, enabled });
  const { data: gerentes = [] } = useQuery({ queryKey: ['siniest', 'filter-gerentes'], queryFn: fetchSiniestFilterGerentes, staleTime: 300000, enabled });
  const { data: zonas = [] } = useQuery({ queryKey: ['siniest', 'filter-zonas'], queryFn: fetchSiniestFilterZonas, staleTime: 300000, enabled });
  const { data: comparativa } = useQuery({ queryKey: ['siniest', 'comparativa-anual'], queryFn: fetchSiniestComparativaAnual, staleTime: 120000, enabled });
  const { data: alertasActivas = [] } = useQuery({ queryKey: ['siniest-alertas-count'], queryFn: fetchSiniestAlertas, staleTime: 60000, retry: false, enabled });
  const unreadAlertas = alertasActivas.length;

  const noData = !statusLoading && !status?.last_import;
  const lastImport = status?.last_import
    ? format(new Date(status.last_import), "d 'de' MMMM 'de' yyyy, HH:mm", { locale: es })
    : null;

  // El chip global de año/fecha es la fuente de verdad: si el usuario elige un
  // año completo desde el filtro global, seguimos ese año aquí también (evita
  // que el chip global se "ignore" mientras el selector local de Año se queda
  // en el valor anterior).
  React.useEffect(() => {
    if (!globalDesde || !globalHasta) return;
    const m = globalDesde.match(/^(\d{4})-01-01$/);
    if (m && globalHasta === `${m[1]}-12-31`) {
      const y = Number(m[1]);
      if (y !== anoFilter) { setAnoFilter(y); setPage(1); }
    }
  }, [globalDesde, globalHasta]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExportPDF = async () => {
    setPdfLoading(true);
    try {
      const params = {};
      if (gerenteFilter) params.gerente_zona = gerenteFilter;
      if (zonaFilter) params.zona = zonaFilter;
      const blob = await exportSiniestPDF(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `siniestralidad_${new Date().toISOString().slice(0,10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF exportado correctamente');
    } catch {
      toast.error('Error al exportar PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  // Chart data
  const mensualChartData = mensual.map(m => ({
    name: m.mes.slice(0, 3),
    'Acc. Baja': m.acc_baja,
    'JP': m.jp,
    'Near Misses': m.near_misses,
  }));
  const zonaChartData = porZona.map(z => ({ name: z.zona, value: z.acc_baja }));
  const tipoChartData = porTipo.map(t => ({
    name: t.tipo.length > 30 ? t.tipo.slice(0, 30) + '…' : t.tipo,
    value: t.count,
    fullName: t.tipo,
  }));
  const riesgoChartData = porRiesgo.map(r => ({ name: r.riesgo, value: r.count }));

  return (
    <div className="flex flex-col min-h-0 h-full overflow-auto" style={{ background: 'var(--vz-bg-primary)' }}>
      <GlobalFilterBar />

      <div className="flex-1 p-4 lg:p-6 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-montserrat font-bold text-lg" style={{ color: 'var(--vz-text-primary)' }}>
              Siniestralidad
            </h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
              {lastImport ? `Último import: ${lastImport} · ${status.kpi_centros} centros, ${status.incidentes} incidentes` : 'Sin datos importados aún'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Year selector */}
            <div className="flex items-center gap-1 px-2 py-1 rounded-lg" style={{ background: 'rgba(74,158,255,0.08)', border: '1px solid rgba(74,158,255,0.2)' }}>
              <Calendar size={11} style={{ color: 'var(--vz-blue)' }} />
              <span className="text-[10px] font-semibold" style={{ color: 'var(--vz-blue)' }}>Año:</span>
              <Select value={String(anoFilter)} onValueChange={v => { setAnoFilter(Number(v)); setPage(1); }}>
                <SelectTrigger className="h-5 text-xs border-0 bg-transparent p-0 pl-1 w-16 focus:ring-0" style={{ color: 'var(--vz-blue)', fontWeight: 700 }} data-testid="siniest-ano-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }}>
                  {[currentYear - 2, currentYear - 1, currentYear].map(y => (
                    <SelectItem key={y} value={String(y)} className="text-xs">{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Month filter */}
            <div className="flex items-center gap-1 px-2 py-1 rounded-lg" style={{ background: 'rgba(74,158,255,0.08)', border: '1px solid rgba(74,158,255,0.2)' }}>
              <span className="text-[10px] font-semibold" style={{ color: 'var(--vz-blue)' }}>Mes:</span>
              <Select value={mesFilter || 'all'} onValueChange={v => { setMesFilter(v === 'all' ? '' : v); setPage(1); }}>
                <SelectTrigger className="h-5 text-xs border-0 bg-transparent p-0 pl-1 w-20 focus:ring-0" style={{ color: 'var(--vz-blue)', fontWeight: 600 }} data-testid="siniest-mes-filter">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }}>
                  <SelectItem value="all" className="text-xs">Todos</SelectItem>
                  {['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'].map((m, i) => (
                    <SelectItem key={i+1} value={String(i+1)} className="text-xs">{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Delegación/Gerencia filter — covers all delegations including Colombia */}
            <Select value={gerenteFilter || 'all'} onValueChange={v => setGerenteFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-7 text-xs w-40" style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-primary)' }} data-testid="siniest-gerente-filter">
                <SelectValue placeholder="Delegación/Gerencia" />
              </SelectTrigger>
              <SelectContent style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }}>
                <SelectItem value="all" className="text-xs">Todas las delegaciones</SelectItem>
                {gerentes.map(g => <SelectItem key={g} value={g} className="text-xs">{g}</SelectItem>)}
              </SelectContent>
            </Select>

            {/* PDF Export */}
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs gap-1.5"
              style={{ background: 'rgba(74,158,255,0.08)', border: '1px solid rgba(74,158,255,0.25)', color: 'var(--vz-blue)' }}
              onClick={handleExportPDF}
              disabled={pdfLoading || noData}
              data-testid="siniest-pdf-btn"
            >
              {pdfLoading
                ? <RefreshCw size={12} className="animate-spin" />
                : <FileDown size={12} />
              }
              {pdfLoading ? 'Exportando...' : 'PDF'}
            </Button>

            {/* Alerts Bell */}
            <button
              className="relative h-7 w-7 rounded-lg flex items-center justify-center transition-colors"
              style={{
                background: unreadAlertas > 0 ? 'rgba(239,83,80,0.1)' : 'var(--vz-bg-elevated)',
                border: `1px solid ${unreadAlertas > 0 ? 'rgba(239,83,80,0.3)' : 'var(--vz-border-subtle)'}`,
              }}
              onClick={() => setShowAlertas(true)}
              data-testid="siniest-alertas-btn"
            >
              <Bell size={13} style={{ color: unreadAlertas > 0 ? '#EF5350' : 'var(--vz-text-muted)' }} />
              {unreadAlertas > 0 && (
                <span
                  className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] font-bold"
                  style={{ background: '#EF5350', color: '#fff' }}
                >
                  {unreadAlertas > 9 ? '9+' : unreadAlertas}
                </span>
              )}
            </button>

            {/* Import */}
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs gap-1.5"
              style={{ background: 'rgba(0,153,64,0.08)', border: '1px solid rgba(0,153,64,0.25)', color: 'var(--vz-green)' }}
              onClick={() => setShowImport(p => !p)}
              data-testid="siniest-import-btn"
            >
              <Upload size={12} />
              Importar Excel
            </Button>
          </div>
        </div>

        {/* Import zone */}
        {showImport && (
          <ImportPanel onImported={() => setShowImport(false)} />
        )}

        {noData ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <FileWarning size={40} style={{ color: 'var(--vz-text-muted)', opacity: 0.4 }} />
            <p className="text-sm font-medium" style={{ color: 'var(--vz-text-muted)' }}>No hay datos importados</p>
            <p className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Importa el Excel con los datos de siniestralidad para empezar</p>
            <Button size="sm" onClick={() => setShowImport(true)} style={{ background: 'var(--vz-green)', color: '#fff' }} data-testid="siniest-import-cta">
              <Upload size={14} className="mr-2" />
              Importar datos
            </Button>
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
              <SiniestKpiCard label="Acc. con Baja" value={kpis?.total_acc_baja ?? '—'} sub={`Año ${kpis?.ano || anoFilter}`} icon={AlertTriangle} color="var(--vz-red)" testId="siniest-kpi-acc-baja" />
              <SiniestKpiCard label="Jornadas Perdidas" value={kpis?.total_jp ?? '—'} icon={Clock} color="var(--vz-orange)" testId="siniest-kpi-jp" />
              <SiniestKpiCard label="Índice Frecuencia" value={kpis?.if_global ?? '—'} sub="Acc/millón horas" icon={Activity} color="var(--vz-blue)" testId="siniest-kpi-if" />
              <SiniestKpiCard label="Índice Gravedad" value={kpis?.ig_global ?? '—'} sub="JP/mil horas" icon={TrendingDown} color="#AB47BC" testId="siniest-kpi-ig" />
              <SiniestKpiCard label="Acc. Sin Baja" value={kpis?.total_acc_sb ?? '—'} icon={AlertTriangle} color="var(--vz-yellow)" testId="siniest-kpi-acc-sb" />
              <SiniestKpiCard label="Near Misses" value={kpis?.total_nm ?? '—'} icon={AlertTriangle} color="#00BCD4" testId="siniest-kpi-nm" />
              <SiniestKpiCard label="HIPO" value={kpis?.total_hipo ?? '—'} sub="Hipotéticos" icon={AlertTriangle} color="#8BC34A" testId="siniest-kpi-hipo" />
              <SiniestKpiCard label="Sedes con datos" value={kpis?.total_centros ?? '—'} sub="Con actividad en el año" icon={Users} color="var(--vz-text-muted)" testId="siniest-kpi-centros" />
            </div>

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <ChartCard title="Evolución Mensual" subtitle="Accidentes con Baja por mes" className="lg:col-span-2">
                <VerticalBarChart
                  data={mensualChartData.map(m => ({ name: m.name, value: m['Acc. Baja'] }))}
                  dataKey="value"
                  color="var(--vz-red)"
                />
              </ChartCard>

              <ChartCard title="Por Tipo de Accidente" subtitle="Distribución Sheet 2">
                <DonutChart
                  data={tipoChartData}
                  dataKey="value"
                  colors={['var(--vz-red)', 'var(--vz-orange)', '#00BCD4', '#AB47BC', 'var(--vz-green)']}
                  centerText={`${porTipo.reduce((s,t)=>s+t.count,0)}`}
                  centerLabel="Total"
                />
              </ChartCard>
            </div>

            {/* Charts Row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ChartCard title="Por Delegación/Gerencia" subtitle="Accidentes con baja por delegación">
                <HorizontalBarChart data={zonaChartData} dataKey="value" color="var(--vz-red)" />
              </ChartCard>
              <ChartCard title="Por Riesgo Crítico" subtitle="Top 10 riesgos identificados">
                <HorizontalBarChart data={riesgoChartData} dataKey="value" color="var(--vz-orange)" />
              </ChartCard>
            </div>

            {/* Comparativa Interanual — only shown when 2+ years of data */}
            {comparativa && comparativa.years && comparativa.years.length >= 1 && (() => {
              const years = comparativa.years;
              const YEAR_COLORS = { 2024: '#888888', 2025: '#FFB300', 2026: '#00C853', 2027: '#4A9EFF' };
              const CHART_STYLE = {
                grid: 'rgba(100,116,139,0.15)',
                axis: 'var(--vz-text-muted)',
                tooltipBg: '#0F2045',
                tooltipBorder: '#1A3A6A',
              };
              const MES_SHORT = { "ENERO":"ENE","FEBRERO":"FEB","MARZO":"MAR","ABRIL":"ABR","MAYO":"MAY","JUNIO":"JUN","JULIO":"JUL","AGOSTO":"AGO","SEPTIEMBRE":"SEP","OCTUBRE":"OCT","NOVIEMBRE":"NOV","DICIEMBRE":"DIC" };
              const mensualMapped = comparativa.mensual.map(r => ({ ...r, mes: MES_SHORT[r.mes] || r.mes }));
              const acumuladoMapped = comparativa.acumulado.map(r => ({ ...r, mes: MES_SHORT[r.mes] || r.mes }));
              const formatVal = v => (typeof v === 'number' ? v.toFixed(2) : v);
              const CustomTip = ({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                return (
                  <div className="rounded-xl p-3 text-xs shadow-xl" style={{ background: CHART_STYLE.tooltipBg, border: `1px solid ${CHART_STYLE.tooltipBorder}` }}>
                    <p className="font-semibold text-white mb-1">{label}</p>
                    {payload.map((p, i) => <p key={i} style={{ color: p.color }}>{p.name}: <strong>{formatVal(p.value)}</strong></p>)}
                  </div>
                );
              };
              return (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 px-1 pt-2">
                    <div className="w-1 h-5 rounded-full" style={{ background: 'var(--vz-blue)' }} />
                    <span className="font-montserrat text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--vz-text-muted)' }}>
                      Comparativa interanual · IF & IG
                    </span>
                    <div className="flex gap-1 ml-2">
                      {years.map(y => (
                        <span key={y} className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: `${YEAR_COLORS[y] || '#888'}22`, color: YEAR_COLORS[y] || '#888', border: `1px solid ${YEAR_COLORS[y] || '#888'}44` }}>{y}</span>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Chart 1: Monthly IF */}
                    <ChartCard title="Índice Frecuencia mensual" subtitle="IF mensual por año (× 1.000.000 h)">
                      <ResponsiveContainer width="100%" height={220}>
                        <ComposedChart data={mensualMapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                          <CartesianGrid stroke={CHART_STYLE.grid} strokeDasharray="3 3" />
                          <XAxis dataKey="mes" tick={{ fill: CHART_STYLE.axis, fontSize: 9 }} tickLine={false} axisLine={false} />
                          <YAxis tick={{ fill: CHART_STYLE.axis, fontSize: 9 }} tickLine={false} axisLine={false} width={36} />
                          <RechartTooltip content={<CustomTip />} />
                          <Legend wrapperStyle={{ fontSize: 10, color: CHART_STYLE.axis }} />
                          {years.map(y => (
                            <Line key={y} type="monotone" dataKey={`IF_${y}`} name={`IF ${y}`} stroke={YEAR_COLORS[y] || '#888'} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} connectNulls />
                          ))}
                        </ComposedChart>
                      </ResponsiveContainer>
                    </ChartCard>

                    {/* Chart 2: Monthly IG */}
                    <ChartCard title="Índice Gravedad mensual" subtitle="IG mensual por año (× 1.000 h)">
                      <ResponsiveContainer width="100%" height={220}>
                        <ComposedChart data={mensualMapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                          <CartesianGrid stroke={CHART_STYLE.grid} strokeDasharray="3 3" />
                          <XAxis dataKey="mes" tick={{ fill: CHART_STYLE.axis, fontSize: 9 }} tickLine={false} axisLine={false} />
                          <YAxis tick={{ fill: CHART_STYLE.axis, fontSize: 9 }} tickLine={false} axisLine={false} width={36} />
                          <RechartTooltip content={<CustomTip />} />
                          <Legend wrapperStyle={{ fontSize: 10, color: CHART_STYLE.axis }} />
                          {years.map(y => (
                            <Line key={y} type="monotone" dataKey={`IG_${y}`} name={`IG ${y}`} stroke={YEAR_COLORS[y] || '#888'} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} connectNulls />
                          ))}
                        </ComposedChart>
                      </ResponsiveContainer>
                    </ChartCard>

                    {/* Chart 3: Accumulated IF */}
                    <ChartCard title="IF Acumulado" subtitle="Índice Frecuencia acumulado por año">
                      <ResponsiveContainer width="100%" height={220}>
                        <ComposedChart data={acumuladoMapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                          <CartesianGrid stroke={CHART_STYLE.grid} strokeDasharray="3 3" />
                          <XAxis dataKey="mes" tick={{ fill: CHART_STYLE.axis, fontSize: 9 }} tickLine={false} axisLine={false} />
                          <YAxis tick={{ fill: CHART_STYLE.axis, fontSize: 9 }} tickLine={false} axisLine={false} width={36} />
                          <RechartTooltip content={<CustomTip />} />
                          <Legend wrapperStyle={{ fontSize: 10, color: CHART_STYLE.axis }} />
                          {years.map(y => (
                            <Line key={y} type="monotone" dataKey={`IF_${y}`} name={`IF Acum. ${y}`} stroke={YEAR_COLORS[y] || '#888'} strokeWidth={2.5} dot={{ r: 2 }} activeDot={{ r: 4 }} connectNulls />
                          ))}
                          <ReferenceLine y={23.7} stroke="#EF5350" strokeDasharray="5 3" strokeWidth={1.5} label={{ value: 'Objetivo', fill: '#EF5350', fontSize: 9, position: 'insideTopRight' }} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </ChartCard>

                    {/* Chart 4: Accumulated IG */}
                    <ChartCard title="IG Acumulado" subtitle="Índice Gravedad acumulado por año">
                      <ResponsiveContainer width="100%" height={220}>
                        <ComposedChart data={acumuladoMapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                          <CartesianGrid stroke={CHART_STYLE.grid} strokeDasharray="3 3" />
                          <XAxis dataKey="mes" tick={{ fill: CHART_STYLE.axis, fontSize: 9 }} tickLine={false} axisLine={false} />
                          <YAxis tick={{ fill: CHART_STYLE.axis, fontSize: 9 }} tickLine={false} axisLine={false} width={36} />
                          <RechartTooltip content={<CustomTip />} />
                          <Legend wrapperStyle={{ fontSize: 10, color: CHART_STYLE.axis }} />
                          {years.map(y => (
                            <Line key={y} type="monotone" dataKey={`IG_${y}`} name={`IG Acum. ${y}`} stroke={YEAR_COLORS[y] || '#888'} strokeWidth={2.5} dot={{ r: 2 }} activeDot={{ r: 4 }} connectNulls />
                          ))}
                        </ComposedChart>
                      </ResponsiveContainer>
                    </ChartCard>
                  </div>
                </div>
              );
            })()}

            {/* Incident table (Sheet 2) */}
            <ChartCard
              title="Listado de Incidentes"
              subtitle={`${propios?.total ?? 0} registros (año ${anoFilter})${status?.incidentes ? ` de ${status.incidentes} importados en total` : ''}${status?.cuarentena ? ` · ${status.cuarentena} en cuarentena por fecha inválida` : ''}`}
              headerExtra={
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Date filters */}
                  <input
                    type="date"
                    value={desdeFilter}
                    onChange={e => { setDesdeFilter(e.target.value); setPage(1); }}
                    className="h-6 text-xs rounded-md px-2"
                    style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-primary)', colorScheme: 'dark' }}
                    data-testid="siniest-desde-filter"
                    title="Fecha desde"
                  />
                  <span className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>—</span>
                  <input
                    type="date"
                    value={hastaFilter}
                    onChange={e => { setHastaFilter(e.target.value); setPage(1); }}
                    className="h-6 text-xs rounded-md px-2"
                    style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-primary)', colorScheme: 'dark' }}
                    data-testid="siniest-hasta-filter"
                    title="Fecha hasta"
                  />
                  <Select value={tipoFilter || 'all'} onValueChange={v => { setTipoFilter(v === 'all' ? '' : v); setPage(1); }}>
                    <SelectTrigger className="h-6 text-xs w-40" style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-primary)' }}>
                      <SelectValue placeholder="Tipo" />
                    </SelectTrigger>
                    <SelectContent style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }}>
                      <SelectItem value="all" className="text-xs">Todos los tipos</SelectItem>
                      {porTipo.map(t => <SelectItem key={t.tipo} value={t.tipo} className="text-xs">{t.tipo}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={lugarFilter || 'all'} onValueChange={v => { setLugarFilter(v === 'all' ? '' : v); setPage(1); }}>
                    <SelectTrigger className="h-6 text-xs w-32" style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-primary)' }} data-testid="siniest-lugar-filter">
                      <SelectValue placeholder="Lugar" />
                    </SelectTrigger>
                    <SelectContent style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }}>
                      <SelectItem value="all" className="text-xs">Todos los lugares</SelectItem>
                      {porLugar.map(l => <SelectItem key={l.name} value={l.name} className="text-xs">{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={gerenteZonaFilter || 'all'} onValueChange={v => { setGerenteZonaFilter(v === 'all' ? '' : v); setPage(1); }}>
                    <SelectTrigger className="h-6 text-xs w-36" style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-primary)' }} data-testid="siniest-inclist-gerente-filter">
                      <SelectValue placeholder="Gerente Zona" />
                    </SelectTrigger>
                    <SelectContent style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }}>
                      <SelectItem value="all" className="text-xs">Todos los gerentes</SelectItem>
                      {gerentes.map(g => <SelectItem key={g} value={g} className="text-xs">{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="relative">
                    <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: 'var(--vz-text-muted)' }} />
                    <Input
                      placeholder="Persona..."
                      value={personaFilter}
                      onChange={e => { setPersonaFilter(e.target.value); setPage(1); }}
                      className="pl-6 h-6 text-xs w-28"
                      style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-primary)' }}
                      data-testid="siniest-persona-filter"
                    />
                  </div>
                  <div className="relative">
                    <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: 'var(--vz-text-muted)' }} />
                    <Input
                      placeholder="Riesgo..."
                      value={search}
                      onChange={e => { setSearch(e.target.value); setPage(1); }}
                      className="pl-6 h-6 text-xs w-28"
                      style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-primary)' }}
                    />
                  </div>
                  {(tipoFilter || lugarFilter || gerenteZonaFilter || personaFilter || search || desdeFilter || hastaFilter) && (
                    <button
                      onClick={() => { setTipoFilter(''); setLugarFilter(''); setGerenteZonaFilter(''); setPersonaFilter(''); setSearch(''); setDesdeFilter(''); setHastaFilter(''); setPage(1); }}
                      className="h-6 px-2 text-xs rounded-md"
                      style={{ background: 'rgba(255,82,82,0.1)', color: 'var(--vz-red)', border: '1px solid rgba(255,82,82,0.2)' }}
                      data-testid="siniest-clear-list-filters"
                    >
                      Limpiar
                    </button>
                  )}
                </div>
              }
            >
              <ScrollArea className="h-[420px]">
                <Table>
                  <TableHeader>
                    <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }}>
                      {['Fecha', 'Sede', 'Persona', 'Tipo', 'Lugar', 'Riesgo Crítico', 'JP', ''].map((h, i) => (
                        <TableHead
                          key={h || i}
                          className="text-[9px] font-montserrat uppercase tracking-wider sticky top-0"
                          style={{ color: 'var(--vz-text-muted)', background: 'var(--vz-bg-card)' }}
                        >
                          {h}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(propios?.data ?? []).map((inc, i) => {
                      const isExp = expandedRow === i;
                      return (
                        <React.Fragment key={i}>
                          <TableRow
                            className="hover:bg-opacity-50 cursor-pointer transition-colors"
                            style={{ borderColor: 'var(--vz-border-subtle)' }}
                            onClick={() => setExpandedRow(isExp ? null : i)}
                            data-testid="siniest-incident-row"
                          >
                            <TableCell className="text-[11px] whitespace-nowrap" style={{ color: 'var(--vz-text-muted)' }}>
                              <div>{inc.fecha_incidente?.slice(0,10) || '—'}</div>
                              {inc.dia_semana && <div className="text-[9px] font-medium" style={{ color: 'var(--vz-blue)', opacity: 0.85 }}>{inc.dia_semana}</div>}
                            </TableCell>
                            <TableCell className="text-[11px] max-w-[150px]" style={{ color: 'var(--vz-text-secondary)' }} title={(inc.centro_trabajo || '') + (inc.codigo_ct ? ` · ${inc.codigo_ct}` : '')}>
                              <div className="leading-tight">
                                <div className="truncate">{inc.centro_trabajo || '—'}</div>
                                {inc.codigo_ct && <div className="text-[9px] font-mono opacity-50">{inc.codigo_ct}</div>}
                              </div>
                            </TableCell>
                            <TableCell className="text-[11px] max-w-[120px] truncate" style={{ color: 'var(--vz-text-primary)' }}>{inc.persona}</TableCell>
                            <TableCell className="text-[11px]">
                              <span style={{ color: TIPO_COLOR[inc.tipo_accidente] || 'var(--vz-text-secondary)' }}>
                                {inc.tipo_accidente?.split('/')[0]?.trim() || '—'}
                              </span>
                            </TableCell>
                            <TableCell className="text-[11px]" style={{ color: 'var(--vz-text-secondary)' }}>{inc.lugar_accidente || '—'}</TableCell>
                            <TableCell className="text-[11px] max-w-[140px] truncate" style={{ color: 'var(--vz-orange)' }}>{inc.riesgo_critico || '—'}</TableCell>
                            <TableCell className="text-[11px] font-mono-custom text-right" style={{ color: inc.jornadas_perdidas > 0 ? 'var(--vz-red)' : 'var(--vz-text-muted)' }}>
                              {inc.jornadas_perdidas ?? 0}
                            </TableCell>
                            <TableCell className="text-center" style={{ color: 'var(--vz-text-muted)' }}>
                              {isExp ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                            </TableCell>
                          </TableRow>
                          {isExp && (
                            <TableRow>
                              <TableCell colSpan={8} className="p-0">
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 px-4 py-3 border-b" style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)' }}>
                                  {[
                                    ['Género', inc.genero],
                                    ['Causa', inc.causa],
                                    ['Fecha Baja', inc.fecha_baja?.slice(0,10)],
                                    ['Fecha Alta', inc.fecha_alta?.slice(0,10)],
                                    ['Gerente Zona', inc.gerente_zona],
                                    ['Día Semana', inc.dia_semana],
                                  ].filter(([, v]) => v).map(([k, v]) => (
                                    <div key={k}>
                                      <p className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: 'var(--vz-text-muted)' }}>{k}</p>
                                      <p className="text-xs mt-0.5" style={{ color: 'var(--vz-text-secondary)' }}>{v}</p>
                                    </div>
                                  ))}
                                  {inc.descripcion && (
                                    <div className="col-span-2 md:col-span-3">
                                      <p className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: 'var(--vz-text-muted)' }}>Descripción</p>
                                      <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'var(--vz-text-secondary)' }}>{inc.descripcion}</p>
                                    </div>
                                  )}
                                  {inc.acciones_mejora && (
                                    <div className="col-span-2 md:col-span-3">
                                      <p className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: 'var(--vz-text-muted)' }}>Acciones de Mejora</p>
                                      <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'var(--vz-green)' }}>{inc.acciones_mejora}</p>
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </ScrollArea>
              {/* Pagination */}
              {propios && propios.total > 50 && (
                <div className="flex items-center justify-between pt-2 px-1">
                  <p className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>
                    Mostrando {(page - 1) * 50 + 1}–{Math.min(page * 50, propios.total)} de {propios.total}
                  </p>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}>Ant.</Button>
                    <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => setPage(p => p+1)} disabled={page * 50 >= propios.total}>Sig.</Button>
                  </div>
                </div>
              )}
            </ChartCard>
          </>
        )}
      </div>

      {/* Alertas Panel Modal */}
      <AlertasPanel open={showAlertas} onClose={() => setShowAlertas(false)} />
    </div>
  );
}
